/**
 * Synthesised sound effects.
 *
 * Everything is generated with oscillators at runtime, so there are no audio
 * files to ship, license or download. Audio only starts after a user gesture,
 * which is both a browser requirement and the polite default.
 */

export type SoundName =
  | 'play'
  | 'trick'
  | 'task'
  | 'hint'
  | 'turn'
  | 'pass'
  | 'deal'
  | 'win'
  | 'fail'
  | 'join'
  | 'click';

interface Tone {
  freq: number;
  /** Seconds from the start of the effect. */
  at: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  /** Slide to this frequency over the note. */
  to?: number;
}

const RECIPES: Record<SoundName, Tone[]> = {
  click: [{ freq: 520, at: 0, dur: 0.05, gain: 0.1 }],
  play: [
    // A short transient over the body makes it read as card on table.
    { freq: 1100, at: 0, dur: 0.03, type: 'square', gain: 0.05 },
    { freq: 320, at: 0.012, dur: 0.08, type: 'triangle', gain: 0.16 },
    { freq: 480, at: 0.05, dur: 0.09, type: 'triangle', gain: 0.1 }
  ],
  pass: [{ freq: 300, at: 0, dur: 0.07, type: 'sine', gain: 0.07, to: 380 }],
  deal: [
    { freq: 240, at: 0, dur: 0.05, type: 'triangle', gain: 0.11 },
    { freq: 262, at: 0.06, dur: 0.05, type: 'triangle', gain: 0.1 },
    { freq: 284, at: 0.12, dur: 0.05, type: 'triangle', gain: 0.1 },
    { freq: 306, at: 0.18, dur: 0.05, type: 'triangle', gain: 0.09 },
    { freq: 330, at: 0.24, dur: 0.05, type: 'triangle', gain: 0.09 },
    { freq: 392, at: 0.31, dur: 0.14, type: 'sine', gain: 0.12 }
  ],
  turn: [
    { freq: 660, at: 0, dur: 0.12, type: 'sine', gain: 0.16 },
    { freq: 990, at: 0.1, dur: 0.16, type: 'sine', gain: 0.12 }
  ],
  trick: [
    { freq: 300, at: 0, dur: 0.14, type: 'sawtooth', gain: 0.09, to: 520 },
    { freq: 620, at: 0.1, dur: 0.18, type: 'sine', gain: 0.13 }
  ],
  task: [
    { freq: 880, at: 0, dur: 0.1, type: 'sine', gain: 0.15 },
    { freq: 1320, at: 0.08, dur: 0.16, type: 'sine', gain: 0.13 }
  ],
  hint: [
    { freq: 720, at: 0, dur: 0.08, type: 'square', gain: 0.07 },
    { freq: 1080, at: 0.07, dur: 0.1, type: 'square', gain: 0.06 }
  ],
  join: [
    { freq: 420, at: 0, dur: 0.09, type: 'sine', gain: 0.13 },
    { freq: 630, at: 0.07, dur: 0.12, type: 'sine', gain: 0.11 }
  ],
  win: [
    { freq: 523, at: 0, dur: 0.16, type: 'triangle', gain: 0.16 },
    { freq: 659, at: 0.13, dur: 0.16, type: 'triangle', gain: 0.16 },
    { freq: 784, at: 0.26, dur: 0.2, type: 'triangle', gain: 0.17 },
    { freq: 1047, at: 0.4, dur: 0.42, type: 'triangle', gain: 0.18 }
  ],
  fail: [
    { freq: 300, at: 0, dur: 0.22, type: 'sawtooth', gain: 0.13, to: 180 },
    { freq: 190, at: 0.2, dur: 0.4, type: 'sawtooth', gain: 0.12, to: 90 }
  ]
};

const STORAGE_KEY = 'crew-online:sound';

class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private warmed = false;

  enabled = ((): boolean => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) !== 'off';
    } catch {
      return true;
    }
  })();

  setEnabled(on: boolean): void {
    this.enabled = on;
    try {
      window.localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
    } catch {
      /* private browsing */
    }
    if (on) this.resume();
  }

  /** Safe to call on any user gesture; browsers require one before audio runs. */
  resume(): void {
    const ctx = this.context();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();
    this.prewarm();
  }

  /**
   * The first oscillator on a fresh context costs several milliseconds to
   * compile, which is heard as a late first sound. Run a silent one up front.
   */
  private prewarm(): void {
    if (this.warmed) return;
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    this.warmed = true;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(gain).connect(master);
      osc.start();
      osc.stop(ctx.currentTime + 0.02);
    } catch {
      /* pre-warming is an optimisation, not a requirement */
    }
  }

  play(name: SoundName): void {
    if (!this.enabled) return;
    const ctx = this.context();
    const master = this.master;
    if (!ctx || !master) return;
    // Schedule even while the context is still starting, rather than dropping
    // the sound; the browser plays it as soon as the graph is running.
    if (ctx.state === 'suspended') void ctx.resume();

    const now = ctx.currentTime;
    for (const tone of RECIPES[name]) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = tone.type ?? 'sine';
      osc.frequency.setValueAtTime(tone.freq, now + tone.at);
      if (tone.to !== undefined) {
        osc.frequency.exponentialRampToValueAtTime(
          Math.max(30, tone.to),
          now + tone.at + tone.dur
        );
      }
      const peak = tone.gain ?? 0.12;
      gain.gain.setValueAtTime(0.0001, now + tone.at);
      gain.gain.exponentialRampToValueAtTime(peak, now + tone.at + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.at + tone.dur);
      osc.connect(gain).connect(master);
      osc.start(now + tone.at);
      osc.stop(now + tone.at + tone.dur + 0.02);
    }
  }

  private context(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor({ latencyHint: 'interactive' });
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.5;
      this.master.connect(this.ctx.destination);
    } catch {
      return null;
    }
    return this.ctx;
  }
}

export const sfx = new Sfx();

/** Unlock and warm audio on the first interaction anywhere in the app. */
export function armAudio(): void {
  const unlock = () => sfx.resume();
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
}
